import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import authService from '@wlb/shared/api/services/authService';
import { isInvalidOtpError } from '@wlb/shared/api/auth/authErrors';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getApiErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import type { ApiError } from '@wlb/shared/types';
import { BrandLogo } from '../components/ui/BrandLogo';
import { FormError } from '../components/ui/FormError';
import { AuthFlowLayout } from '../components/layout/AuthFlowLayout';
import { OtpInput } from '../features/auth/OtpInput';
import type { AuthFlowState } from '../features/auth/authFlowState';
import '../features/auth/auth.css';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

export function VerificationCode() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as AuthFlowState | null;
  const email = state?.email ?? '';

  const [passwordResetToken, setPasswordResetToken] = useState(state?.passwordResetToken ?? '');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }
    const timer = window.setTimeout(() => setCooldown(seconds => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  if (!email) {
    return <Navigate to="/forgot-password" replace />;
  }

  const verify = async (code: string) => {
    if (submittingRef.current) {
      return;
    }
    if (code.length < OTP_LENGTH) {
      setError(t('auth.verificationCode.codeIncomplete', { length: OTP_LENGTH }));
      return;
    }
    setError('');
    submittingRef.current = true;
    setLoading(true);
    try {
      await authService.verifyPasswordResetOtp({ otpCode: code, passwordResetToken });
      const next: AuthFlowState = { email, passwordResetToken, otpCode: code };
      navigate('/reset-password', { state: next });
    } catch (err) {
      if (isInvalidOtpError(err)) {
        setError(t('auth.verificationCode.invalidCode'));
        setOtpCode('');
      } else {
        ToastService.show({ type: 'error', message: getApiErrorMessage(err as ApiError) });
      }
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    verify(otpCode);
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const response = await authService.forgotPassword({ email });
      // The API issues a fresh token with each code; keep the newest one.
      setPasswordResetToken(response.data?.passwordResetToken ?? passwordResetToken);
      setOtpCode('');
      setError('');
      setCooldown(RESEND_COOLDOWN_SECONDS);
      ToastService.show({ type: 'success', message: t('auth.verificationCode.codeResent') });
    } catch (err) {
      ToastService.show({ type: 'error', message: getApiErrorMessage(err as ApiError) });
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthFlowLayout>
      <div className="auth-flow__card">
        <button className="auth-flow__back" type="button" onClick={() => navigate('/forgot-password')}>
          ‹ {t('common.buttons.back')}
        </button>
        <BrandLogo compact className="auth-flow__brand" />
        <h1 className="auth-flow__heading">{t('auth.verificationCode.heading')}</h1>
        <p className="auth-flow__subheading">
          {t('auth.verificationCode.subheadingPrefix')} {email}{' '}
          {t('auth.verificationCode.subheadingSuffix')}
        </p>

        <form className="auth-flow__form" onSubmit={handleSubmit} noValidate>
          <div className="auth-flow__field">
            <OtpInput
              value={otpCode}
              length={OTP_LENGTH}
              onChange={setOtpCode}
              onComplete={code => {
                verify(code);
              }}
              disabled={loading}
              invalid={Boolean(error)}
              describedBy={error ? 'verification-code-error' : undefined}
            />
            <FormError message={error} id="verification-code-error" />
          </div>
          <button
            className="auth-flow__primary"
            type="submit"
            disabled={loading || otpCode.length < OTP_LENGTH}
          >
            {loading ? t('common.loading') : t('auth.verificationCode.continue')}
          </button>
        </form>

        <p className="auth-flow__footer">
          <button
            className="auth-flow__link-button"
            type="button"
            onClick={handleResend}
            disabled={resending || cooldown > 0}
          >
            {cooldown > 0
              ? t('auth.verificationCode.resendIn', { seconds: cooldown })
              : t('auth.verificationCode.resendCode')}
          </button>
        </p>
      </div>
    </AuthFlowLayout>
  );
}
